import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalDialog } from '../../../modal/layout/ModalDialog';
import { ModalHeader } from '../../../modal/layout/ModalHeader';
import { ModalScrollableLayout } from '../../../modal/layout/ModalScrollableLayout';
import { useProtectedClose } from '../../../modal/hooks/useProtectedClose';
import {
    NOTIFICATION_VARIANT,
    NotificationsContainer,
    useNotifications,
} from '../../../notifications/Notifications';
import type { AttributeEntityType, AttributeEntry } from '../contracts';
import { formatAttributeValueType } from '../contracts';
import { getAttributeModalContentComponent } from '../modalContent/valueTypeModalContentMap';
import type { AttributeModalFormState } from '../modalContent/types';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';

interface AttributeModalProps {
    entry: AttributeEntry;
    displayLabel: string;
    entityType: AttributeEntityType;
    entityId: string;
    onClose: () => void;
    onAfterMutation: () => Promise<void>;
}

export function AttributeModal({
    entry,
    displayLabel,
    entityType,
    entityId,
    onClose,
    onAfterMutation,
}: AttributeModalProps) {
    const allowNextNavigationRef = useRef(false);
    const scrollBodyRef = useRef<HTMLDivElement>(null);
    const { addNotification, clearNotifications } = useNotifications();
    const [formState, setFormState] = useState<AttributeModalFormState>({
        isDirty: false,
        isBusy: false,
    });
    const closeRoute = useCallback(() => {
        allowNextNavigationRef.current = true;
        onClose();
    }, [onClose]);
    const { attemptClose, isShaking, showWarning } = useProtectedClose({
        isOpen: true,
        shouldWarn: formState.isDirty,
        onClose: closeRoute,
        canClose: !formState.isBusy,
    });
    const Content = getAttributeModalContentComponent(entry.valueType);
    const scrollToBodyTop = useCallback(() => {
        if (scrollBodyRef.current) {
            scrollBodyRef.current.scrollTop = 0;
        }
    }, []);

    useUnsavedChangesGuard({
        shouldWarn: formState.isDirty,
        allowNextNavigationRef,
        onWarning: scrollToBodyTop,
    });

    useEffect(() => {
        if (showWarning) {
            scrollToBodyTop();
            addNotification({
                message: gettext('UNSAVED_CHANGES_CLOSE_AGAIN'),
                variant: NOTIFICATION_VARIANT.WARNING,
            });
        } else {
            clearNotifications();
        }
    }, [addNotification, clearNotifications, scrollToBodyTop, showWarning]);

    return (
        <ModalDialog
            open={true}
            onClose={attemptClose}
            canClose={!formState.isBusy}
            closeOnClickOutside={true}
            closeOnEsc={true}
            shake={isShaking}
            className="max-w-4xl rounded-2xl"
            aria-label={displayLabel}
        >
            <ModalScrollableLayout>
                <ModalHeader
                    onClose={attemptClose}
                    title={displayLabel}
                    isLoading={formState.isBusy}
                />
                <Content
                    entry={entry}
                    entityType={entityType}
                    entityId={entityId}
                    onClose={attemptClose}
                    onAfterMutation={onAfterMutation}
                    onFormStateChange={setFormState}
                    scrollBodyRef={scrollBodyRef}
                    bodyIntro={
                        <>
                            <div className="mb-5 grid gap-3 px-6 text-sm text-gray-600 md:grid-cols-2">
                                <div className="rounded-lg bg-gray-50 p-3">
                                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                                        {gettext('ATTRIBUTE_TYPE_FIELD')}
                                    </span>
                                    <span className="font-semibold text-gray-800">
                                        {formatAttributeValueType(entry.valueType)}
                                    </span>
                                </div>
                                <div className="rounded-lg bg-gray-50 p-3">
                                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                                        {gettext('ATTRIBUTE_PERSISTENCE_LABEL')}
                                    </span>
                                    <span className="font-semibold text-gray-800">
                                        {gettext('ATTRIBUTE_EXPLICIT_SAVE_LABEL')}
                                    </span>
                                </div>
                            </div>
                            <div className="px-6 pb-4">
                                <NotificationsContainer />
                            </div>
                        </>
                    }
                />
            </ModalScrollableLayout>
        </ModalDialog>
    );
}
